// Mirrors server/services/csv-headers; a parity test pins the two together.
// Imported statically, not via i18next: normalization runs inside a CSV parser.

import i18n from '@app/i18n';
// From locale-constants, not the barrel: suites that mock '@app/i18n' omit this.
import { SUPPORTED_LOCALES } from '@app/i18n/locale-constants';
import enLabels from '@app/i18n/locales/en/csvHeaders.json';
import frCALabels from '@app/i18n/locales/fr-CA/csvHeaders.json';

export type CsvColumnKey = keyof typeof enLabels;

// These strings are the contract: downstream code and saved files key off them.
export const ENGLISH_LABELS: Record<CsvColumnKey, string> = enLabels;

export const CANONICAL_KEYS = Object.keys(enLabels) as CsvColumnKey[];

const BUNDLES: Record<string, Record<string, string>> = {
  en: enLabels,
  'fr-CA': frCALabels,
};

// Keyed off SUPPORTED_LOCALES so a missing bundle fails the parity test.
export const LABELS_BY_LOCALE: Record<
  string,
  Record<string, string>
> = Object.fromEntries(
  SUPPORTED_LOCALES.filter((locale) => BUNDLES[locale]).map((locale) => [
    locale,
    BUNDLES[locale],
  ]),
);

const DEFAULT_LOCALE = 'en';

// Without the BOM, Excel guesses the local codepage and mangles accents.
export const UTF8_BOM = '\uFEFF';

// Response.text() in doRequest strips the server's BOM, so downloads re-add it.
export const withBom = (csv: string): string =>
  `${UTF8_BOM}${csv.replace(/^\uFEFF/, '')}`;

// Column order of each generated file.
export const CSV_CATALOG = {
  MESSAGE_TEMPLATE_IMPORT: [
    'branding',
    'businessSegment',
    'lineOfBusiness',
    'lineOfBusinessName',
    'templateName',
    'message',
    'exportId',
  ],
  MESSAGE_TEMPLATE_ORG_IMPORT: ['templateName', 'message'],
  BULK_USERS: [
    'uniqueId',
    'firstName',
    'lastName',
    'email',
    'title',
    'phoneNumber',
    'officeLine',
    'roles',
    'groupMembers',
    'groupLeads',
  ],
  CHANGE_UNIQUE_IDS: ['oldUniqueId', 'newUniqueId'],
  // The users export adds columns the upload template does not carry.
  BULK_USERS_EXPORT: [
    'uniqueId',
    'firstName',
    'lastName',
    'title',
    'lastLogin',
    'phoneNumber',
    'officeLine',
    'email',
    'roles',
    'groupMembers',
    'groupLeads',
    'active',
  ],
} satisfies Record<string, CsvColumnKey[]>;

// Folds away everything a hand-retyped or Excel-saved header can differ by.
export const comparisonKey = (label: string | undefined): string =>
  String(label ?? '')
    .replace(/\uFEFF/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036F]/g, '')
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

// English goes last so it wins any collision with a future locale's label.
const ALIASES = new Map<string, CsvColumnKey>();
[...Object.values(LABELS_BY_LOCALE), ENGLISH_LABELS].forEach((labels) => {
  Object.entries(labels).forEach(([canonicalKey, label]) => {
    if (!CANONICAL_KEYS.includes(canonicalKey as CsvColumnKey)) return;
    ALIASES.set(comparisonKey(label), canonicalKey as CsvColumnKey);
  });
});

export const normalizeHeader = (
  label: string | undefined,
): CsvColumnKey | null => ALIASES.get(comparisonKey(label)) ?? null;

// Unknown columns are dropped; a repeated column keeps its last occurrence.
export const normalizeColumns = (
  columns: Array<string | undefined> = [],
): Partial<Record<CsvColumnKey, number>> => {
  const indices: Partial<Record<CsvColumnKey, number>> = {};
  columns.forEach((column, index) => {
    const canonicalKey = normalizeHeader(column);
    if (canonicalKey) {
      indices[canonicalKey] = index;
    }
  });
  return indices;
};

export const normalizeRow = (
  row: Record<string, string>,
): Partial<Record<CsvColumnKey, string>> => {
  const normalized: Partial<Record<CsvColumnKey, string>> = {};
  Object.entries(row).forEach(([label, value]) => {
    const canonicalKey = normalizeHeader(label);
    if (canonicalKey) {
      normalized[canonicalKey] = value;
    }
  });
  return normalized;
};

const activeLocale = () => i18n.language || DEFAULT_LOCALE;

export const headerLabel = (
  canonicalKey: CsvColumnKey,
  locale: string = activeLocale(),
): string =>
  LABELS_BY_LOCALE[locale]?.[canonicalKey] ??
  LABELS_BY_LOCALE[DEFAULT_LOCALE][canonicalKey];

export const localizedHeaders = (
  canonicalKeys: readonly CsvColumnKey[],
  locale: string = activeLocale(),
): string[] => canonicalKeys.map((key) => headerLabel(key, locale));

export const englishHeaders = (
  canonicalKeys: readonly CsvColumnKey[],
): string[] => canonicalKeys.map((key) => ENGLISH_LABELS[key]);

// Unknown keys pass through, so the error report's appended columns survive.
export const localizeLabelKeys = <T>(
  row: Record<string, T>,
  locale: string = activeLocale(),
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(row).map(([label, value]) => {
      const canonicalKey = normalizeHeader(label);
      return [canonicalKey ? headerLabel(canonicalKey, locale) : label, value];
    }),
  );
